// VIBRANIAM-SPACE v2 - ModuleNode.jsx
// A node box: header, declared ports, and one control per parameter. Every
// modulatable control carries its own port dot on the left edge, so dragging a
// cable onto a slider is what creates a modulation slot.

import React, { useCallback } from 'react';
import { NODE_TYPES } from '../engine/nodes/index.js';
import { kindColor } from '../engine/types.js';
import { FORCE_KINDS } from '../engine/forces.js';
import {
  NODE_WIDTH, HEADER_H, PORT_ROW_H, PORTS_PAD,
  controlsOf, declaredPorts, portsSectionHeight,
} from './node-layout.js';

// Helper functions for React.memo comparator
function shallowEqual(objA, objB) {
  if (objA === objB) return true;
  if (typeof objA !== 'object' || objA === null || typeof objB !== 'object' || objB === null) return false;
  const keysA = Object.keys(objA);
  const keysB = Object.keys(objB);
  if (keysA.length !== keysB.length) return false;
  for (let key of keysA) {
    if (!Object.prototype.hasOwnProperty.call(objB, key)) return false;
    if (objA[key] !== objB[key]) return false;
  }
  return true;
}

function edgesEqual(edgesA, edgesB) {
  if (edgesA === edgesB) return true;
  if (edgesA.length !== edgesB.length) return false;
  const setA = new Set(edgesA.map(e => e.id));
  const setB = new Set(edgesB.map(e => e.id));
  if (setA.size !== setB.size) return false;
  for (const id of setA) {
    if (!setB.has(id)) return false;
  }
  return true;
}

function PortDot({ kind, dir, filled, onDown, onUp, title }) {
  const color = kindColor(kind);
  return (
    <button
      type="button"
      title={title}
      onPointerDown={(e) => { e.stopPropagation(); if (dir === 'out') onDown(e); }}
      onPointerUp={(e) => { e.stopPropagation(); if (dir === 'in') onUp(e); }}
      className="h-2.5 w-2.5 shrink-0 rounded-full border transition-transform hover:scale-150"
      style={{ background: filled ? color : 'transparent', borderColor: color }}
    />
  );
}

function Control({ node, field, value, modulated, modCount, onChange, onStartLink, onFinishLink }) {
  const stop = (e) => e.stopPropagation();
  const type = field.type || 'range';

  const dot = (
    <span className="absolute -left-[5px] top-[4px]">
      <PortDot
        kind="scalar"
        dir="in"
        filled={modCount > 0}
        onUp={(e) => onFinishLink(e, node.id, `p:${field.key}`)}
        onDown={() => {}}
        title={`${field.label} — trascina qui un segnale per modularlo`}
      />
    </span>
  );

  if (type === 'select' || type === 'forceKind') {
    const options = type === 'forceKind'
      ? FORCE_KINDS.map((f) => ({ value: f.kind, label: f.label }))
      : (field.options || []).map((o) => ({ value: o, label: String(o) }));
    return (
      <label className="relative block" style={{ height: 36 }}>
        <span className="mb-0.5 block text-[9px] uppercase tracking-wider text-slate-500">{field.label}</span>
        <select
          value={value}
          onPointerDown={stop}
          onChange={(e) => {
            const raw = e.target.value;
            const n = Number(raw);
            onChange(field.key, raw !== '' && !Number.isNaN(n) ? n : raw);
          }}
          className="h-[17px] w-full rounded bg-ink-600 px-1 text-[10px] leading-none text-slate-200 outline-none focus:ring-1 focus:ring-vibe-cyan"
        >
          {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </label>
    );
  }

  if (type === 'bool') {
    return (
      <label className="relative flex items-center justify-between" style={{ height: 22 }}>
        <span className="text-[9px] uppercase tracking-wider text-slate-500">{field.label}</span>
        <input
          type="checkbox"
          checked={!!value}
          onPointerDown={stop}
          onChange={(e) => onChange(field.key, e.target.checked)}
          className="h-3 w-3 accent-vibe-cyan"
        />
      </label>
    );
  }

  if (type === 'color') {
    return (
      <label className="relative block" style={{ height: 36 }}>
        <span className="mb-0.5 block text-[9px] uppercase tracking-wider text-slate-500">{field.label}</span>
        <input
          type="color"
          value={value || '#05060a'}
          onPointerDown={stop}
          onChange={(e) => onChange(field.key, e.target.value)}
          className="h-[17px] w-full cursor-pointer rounded border-0 bg-ink-600 p-0"
        />
      </label>
    );
  }

  // Custom control for image strobe images
  if (type === 'custom' && node.type === 'r.imageStrobe' && field.key === 'images') {
    return (
      <div className="relative block" style={{ height: 80 }}>
        <span className="mb-0.5 block text-[9px] uppercase tracking-wider text-slate-500">{field.label}</span>
        <button
          onPointerDown={stop}
          onClick={() => {
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = 'image/jpeg,image/png,image/webp,image/gif';
            input.multiple = true;
            input.onChange = (e) => {
              const files = Array.from(e.target.files);
              const promises = files.map(file => {
                return new Promise((resolve, reject) => {
                  const reader = new FileReader();
                  reader.onload = () => {
                    resolve({ url: reader.result, weight: 1 });
                  };
                  reader.onerror = reject;
                  reader.readAsDataURL(file);
                });
              });
              Promise.all(promises).then((images) => {
                onChange(field.key, images);
              });
            };
            input.click();
          }}
          className="w-full h-[60px] rounded border-2 border-dashed border-vibe-cyan/50 bg-ink-600/50 text-center text-[10px] text-vibe-cyan hover:bg-ink-600"
        >
          Select Images
        </button>
        {value && value.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {value.map((img, index) => (
              <div key={index} className="relative w-20 h-20">
                <img
                  src={img.url}
                  alt=""
                  className="w-full h-full object-cover rounded"
                />
                <button
                  onClick={() => {
                    const newValue = [...value];
                    newValue.splice(index, 1);
                    onChange(field.key, newValue);
                  }}
                  className="absolute -top-1 -right-1 rounded-full bg-red-500/70 text-[8px] text-white hover:bg-red-500"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  const num = Number(value);
  const decimals = (field.step ?? 1) < 0.01 ? 4 : (field.step ?? 1) < 1 ? 2 : 0;
  return (
    <label className="relative block" style={{ height: 32 }}>
      {dot}
      <div className="flex items-baseline justify-between">
        <span className={`text-[9px] uppercase tracking-wider ${modCount ? 'text-vibe-cyan' : 'text-slate-500'}`}>
          {field.label}{modCount > 1 ? ` ×${modCount}` : ''}
        </span>
        <span className="font-mono text-[9px] text-slate-300">
          {Number.isFinite(num) ? num.toFixed(decimals) : String(value)}
          {modulated !== null && modulated !== undefined && Math.abs(modulated - num) > 1e-6 && (
            <span className="ml-1 text-vibe-cyan">→{modulated.toFixed(decimals)}</span>
          )}
        </span>
      </div>
      <div className="relative">
        <input
          type="range"
          min={field.min} max={field.max} step={field.step}
          value={Number.isFinite(num) ? num : field.min ?? 0}
          onPointerDown={stop}
          onChange={(e) => onChange(field.key, Number(e.target.value))}
          className="w-full"
        />
        {modulated !== null && modulated !== undefined && field.max > field.min && (
          <span
            className="pointer-events-none absolute top-[-1px] h-[5px] w-[2px] rounded bg-vibe-cyan"
            style={{ left: `${((modulated - field.min) / (field.max - field.min)) * 100}%` }}
          />
        )}
      </div>
    </label>
  );
}

function ModuleNode({
  node, selected, edges, collapsed,
  liveParams,
  onSelect, onDragStart, onRemove, onDuplicate, onParamChange,
  onStartLink, onFinishLink, onToggleCollapse,
}) {
  const def = NODE_TYPES[node.type];
  const { inputs, outputs } = declaredPorts(node);
  const fields = controlsOf(node);

  // Defensive: a node whose type no longer exists (e.g. an old saved patch
  // after a module was renamed/removed) must render as an inert placeholder,
  // never throw - an uncaught error here would take the whole tree down.
  if (!def) {
    return (
      <div
        className="node-shadow absolute select-none rounded-lg border border-red-500/40 bg-ink-700/95 px-2 py-2 text-[10px] text-red-300"
        style={{ left: node.x, top: node.y, width: NODE_WIDTH }}
        onPointerDown={(e) => { onSelect(node.id); onDragStart(e, node.id); }}
      >
        <div className="mb-1 font-semibold">Modulo sconosciuto</div>
        <div className="mb-1.5 truncate font-mono text-[9px] opacity-70">{node.type}</div>
        <button type="button" onPointerDown={(e) => e.stopPropagation()} onClick={() => onRemove(node.id)}
          className="rounded border border-red-400/40 px-1.5 py-0.5 text-[9px] hover:bg-red-500/20">
          Rimuovi
        </button>
      </div>
    );
  }
  const accent = def.accent;

  const countInto = useCallback(
    (portId) => edges.filter((e) => e.to.node === node.id && e.to.port === portId).length,
    [edges, node.id]
  );
  const hasOut = useCallback(
    (portId) => edges.some((e) => e.from.node === node.id && e.from.port === portId),
    [edges, node.id]
  );

  const handleParam = useCallback((k, v) => onParamChange(node.id, k, v), [node.id, onParamChange]);

  return (
    <div
      className={`node-shadow absolute select-none rounded-lg border bg-ink-700/95 backdrop-blur ${
        selected ? 'border-vibe-cyan' : 'border-white/10'
      }`}
      style={{ left: node.x, top: node.y, width: NODE_WIDTH }}
      onPointerDown={(e) => { onSelect(node.id); onDragStart(e, node.id); }}
    >
      <div
        className="flex cursor-grab items-center justify-between rounded-t-lg px-2"
        style={{ height: HEADER_H, background: `${accent}22`, borderBottom: `1px solid ${accent}44` }}
        title={def.hint || def.title}
      >
        <div className="flex items-center gap-1.5 overflow-hidden">
          <span className="h-2 w-2 shrink-0 rounded-sm" style={{ background: accent }} />
          <span className="truncate text-[11px] font-semibold text-slate-100">{def.title}</span>
        </div>
        <div className="flex items-center gap-0.5">
          <button type="button" title="Duplica"
            onPointerDown={(e) => e.stopPropagation()} onClick={() => onDuplicate(node.id)}
            className="rounded px-1 text-[10px] leading-none text-slate-400 hover:bg-white/10 hover:text-slate-100">⧉</button>
          <button type="button" title={collapsed ? 'Espandi' : 'Comprimi'}
            onPointerDown={(e) => e.stopPropagation()} onClick={() => onToggleCollapse(node.id)}
            className="rounded px-1 text-[11px] leading-none text-slate-400 hover:bg-white/10 hover:text-slate-100">
            {collapsed ? '+' : '−'}
          </button>
          <button type="button" title="Elimina"
            onPointerDown={(e) => e.stopPropagation()} onClick={() => onRemove(node.id)}
            className="rounded px-1 text-[11px] leading-none text-slate-400 hover:bg-red-500/20 hover:text-red-300">×</button>
        </div>
      </div>

      {(inputs.length > 0 || outputs.length > 0) && (
        <div className="flex justify-between px-2" style={{ paddingTop: PORTS_PAD }}>
          <div className="flex flex-1 flex-col">
            {inputs.map((p) => (
              <div key={p.id} className="flex items-center gap-1.5" style={{ height: PORT_ROW_H, marginLeft: -5 }}>
                <PortDot
                  kind={p.kind} dir="in" filled={countInto(p.id) > 0}
                  onUp={(e) => onFinishLink(e, node.id, p.id)} onDown={() => {}}
                  title={`${p.label} (${p.kind}${p.multi ? ', multiplo' : ''})`}
                />
                <span className="truncate text-[9px] uppercase tracking-wider text-slate-400">
                  {p.label}{p.multi && countInto(p.id) > 0 ? ` ${countInto(p.id)}` : ''}
                </span>
              </div>
            ))}
          </div>
          <div className="flex flex-1 flex-col items-end">
            {outputs.map((p) => (
              <div key={p.id} className="flex flex-row-reverse items-center gap-1.5" style={{ height: PORT_ROW_H, marginRight: -5 }}>
                <PortDot
                  kind={p.kind} dir="out" filled={hasOut(p.id)}
                  onDown={(e) => onStartLink(e, node.id, p.id)} onUp={() => {}}
                  title={`${p.label} (${p.kind})`}
                />
                <span className="truncate text-[9px] uppercase tracking-wider text-slate-400">{p.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {!collapsed && fields.length > 0 && (
        <div className="px-2 pb-2.5" style={{ paddingTop: 6 }}>
          {fields.map((f) => (
            <Control
              key={f.key}
              node={node}
              field={f}
              value={node.params[f.key]}
              modulated={liveParams ? liveParams[f.key] : null}
              modCount={f.mod ? countInto(`p:${f.key}`) : 0}
              onChange={handleParam}
              onStartLink={onStartLink}
              onFinishLink={onFinishLink}
            />
          ))}
        </div>
      )}

      {collapsed && portsSectionHeight(node) === 0 && <div style={{ height: 8 }} />}
    </div>
  );
}

// Memoize ModuleNode to prevent re-renders when props haven't changed meaningfully
export default React.memo(ModuleNode, (prevProps, nextProps) => {
  // Compare node position
  if (prevProps.node.x !== nextProps.node.x || prevProps.node.y !== nextProps.node.y) {
    return false;
  }
  // Compare selection and collapsed state
  if (prevProps.selected !== nextProps.selected || prevProps.collapsed !== nextProps.collapsed) {
    return false;
  }
  // Compare node.params (the base parameters)
  if (!shallowEqual(prevProps.node.params, nextProps.node.params)) {
    return false;
  }
  // Compare liveParams (modulation values)
  if (!shallowEqual(prevProps.liveParams, nextProps.liveParams)) {
    return false;
  }
  // Compare edges (connections)
  if (!edgesEqual(prevProps.edges, nextProps.edges)) {
    return false;
  }
  return true;
});
